import React, { JSX } from 'react';
import { lzFInitials } from '@lz/services/String';
import { LzNCrudField, LzTCrudFieldGroup } from './Field';
import LzMMedia from '../commons/Media';
import LzMOption from '../commons/Option';
import LzXMedia from '@lz/components/commons/Media';
import { LzTFunctionSimple, LzNCrudMediaParams } from '@lz/typings';

export type LzTCrudInputGroup = {
  [key: string]: LzNCrudInput;
};

export type LzTCrudInputType =
  | 'static'
  | 'text'
  | 'textarea'
  | 'autocomplete'
  | 'richeditor'
  | 'email'
  | 'number'
  | 'password'
  | 'color'
  | 'date'
  | 'switch'
  | 'select'
  | 'tags'
  | 'media';

export interface LzNCrudInput {
  label: string;
  icon?: JSX.Element;
  type?: LzTCrudInputType;
  subType?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  hintText?: string;
  /**
   * Set to true if you do not want to show this field.
   * (could have been added for some other purpose, like to use the `preserveValueAfterSubmit` flag).
   */
  skip?: boolean;
  translatable?: boolean;
  required?: boolean;
  multiline?: boolean;
  lazyLoad?: boolean;
  maxLimit?: number;
  default?: any;
  options?: LzMOption[];
  displayFunction?: (
    value: any,
    record?: any,
    key?: string,
    onChange?: (updated: any) => void,
    onReload?: LzTFunctionSimple
  ) => any;
  isVisibleFunction?: (value?: any, record?: any, key?: string) => boolean;
  onChange?: (name: string, value: string) => void;
  mediaParams?: LzNCrudMediaParams;
  isMedia?: boolean;
  isReactNode?: boolean;
  preserveValueAfterSubmit?: boolean;
  gridSpanSm?: number;
  gridSpanMd?: number;
}

class LzMCrudInput implements LzNCrudInput {
  label: string;
  icon?: JSX.Element;
  type: LzTCrudInputType;
  subType: string;
  variant: 'outlined' | 'filled' | 'standard';
  hintText: string;
  skip: boolean;
  translatable: boolean;
  required: boolean;
  multiline: boolean;
  lazyLoad: boolean;
  maxLimit: number;
  default: any;
  options: LzMOption[] | undefined;
  displayFunction: (
    value: any,
    record?: any,
    key?: string,
    onChange?: (updated: any) => void,
    onReload?: LzTFunctionSimple
  ) => any;
  isVisibleFunction: (value?: any, record?: any, key?: string) => boolean;
  onChange?: (name: string, value: string) => void;
  mediaParams?: LzNCrudMediaParams;
  isMedia: boolean;
  isReactNode: boolean;
  preserveValueAfterSubmit: boolean;
  gridSpanSm: number;
  gridSpanMd: number;

  constructor(model: LzNCrudInput, field?: LzNCrudField, isView?: boolean) {
    this.label = model.label || '';
    this.icon = model.icon;

    if (model.type) {
      this.type = model.type;
    } else if (isView) {
      this.type = 'static';
    } else if (!isView && model.isReactNode) {
      this.type = 'static';
    } else if (field?.type === 'reference') {
      this.type = 'select';
    } else if (field?.type === 'media') {
      this.type = 'media';
    } else if (field?.type === 'number') {
      this.type = 'number';
    } else {
      this.type = 'text';
    }

    this.subType = model.subType || '';
    this.variant = model.variant || 'standard';
    this.hintText = model.hintText || '';
    this.skip = !!model?.skip;
    this.translatable =
      typeof model.translatable === 'boolean'
        ? model.translatable
        : !!field?.translatable;
    this.required =
      typeof model.required === 'boolean'
        ? model.required
        : isView
          ? false
          : !!field?.required;
    this.multiline = !!model?.multiline;
    this.lazyLoad = !!model?.lazyLoad;
    this.maxLimit = +(model?.maxLimit || 0);
    this.default = model.default ? model.default : field?.default;
    this.options = model.options ? model.options : field?.options;
    this.displayFunction = LzMCrudInput.getDisplayFunction(model, field);
    this.isVisibleFunction = model.isVisibleFunction
      ? model.isVisibleFunction
      : () => true;
    this.onChange = model.onChange;
    this.mediaParams = model.mediaParams;
    this.isMedia = !!model.mediaParams; // save as a class var instead of making a getter because later this.mediaParams could be initialized to something and then it will not remain null
    this.isReactNode = !!model.isReactNode;
    this.preserveValueAfterSubmit = !!model.preserveValueAfterSubmit;
    this.gridSpanSm = model.gridSpanSm || 6;
    this.gridSpanMd = model.gridSpanMd || 4;
  }

  static getDisplayFunction(model: LzNCrudInput, field?: LzNCrudField) {
    if (model && model.displayFunction) {
      return model.displayFunction;
    }

    if (
      field?.type === 'reference' &&
      typeof field?.refAppendedDisplayField === 'string' &&
      field.refAppendedDisplayField !== ''
    ) {
      return (val: any, record?: any) =>
        typeof field.refAppendedDisplayField === 'string' &&
          record[field?.refAppendedDisplayField]
          ? record[field.refAppendedDisplayField]
          : val === 0
            ? ''
            : val;
    }

    if (model.type === 'switch') {
      return (val: boolean) => (val ? 'Yes' : 'No');
    }

    if (field?.type === 'media') {
      return (val: LzMMedia, record?: any) => {
        const altField = model.mediaParams?.altFieldName || '';
        const altValue = '' === altField ? '' : record[altField] || '';
        const initials = '' === altValue ? '' : lzFInitials(altValue);

        return (
          <LzXMedia
            isAvatar={!!model.mediaParams?.isAvatar}
            avatarSize={model.mediaParams?.avatarSize || 'large'}
            avatarInitials={initials}
            altText={altValue}
            imgSrc={val ? val.file_preview_path : ''}
            className='lz-card-media-small'
          />
        );
      };
    }

    return (val: any) => val;
  }

  static parseData(
    data: any,
    fields: LzTCrudFieldGroup,
    isView?: boolean
  ): LzTCrudInputGroup {
    const out: LzTCrudInputGroup = {};

    if (!data) {
      return out;
    }

    const keys = Object.keys(data);
    for (const key of keys) {
      out[key] = new LzMCrudInput(
        data[key],
        fields ? fields[key] : undefined,
        isView
      );
    }

    return out;
  }
}

export default LzMCrudInput;
